import { useState, useEffect } from 'react';
import { Download, TrendingUp, TrendingDown, DollarSign, Calendar } from 'lucide-react';
import { adminApi } from '../utils/api';
import Layout from '../components/Layout';
import Header from '../components/Header';
import { Button, Card, Select, Badge, Spinner, ErrorState, type BadgeVariant } from '../components/ui';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';
import { planToBadgeVariant } from '../utils/statusBadge';
import type { User, PlanType } from '../types';

interface AdminRevenuePageProps {
  user: User;
}

interface PlanStats {
  plan_type: PlanType;
  user_count: number;
  monthly_price: number;
  total_revenue: number;
}

interface MonthlyRevenue {
  month: string;
  total_revenue: number;
  total_cost: number;
  gpu_cost: number;
  gpu_cost_is_actual: boolean;
  user_count: number;
  transcription_count: number;
}

interface EndpointCost {
  name: string;
  amount: number;
  gpu_hours: number;
}

interface CostBreakdown {
  gpu: number;
  /**
   * runpod_actual = RunPod Billing API の実費（アカウント全体・管理者や dev の検証分も含む）。
   * estimated = 実費を取得できなかったときの推定で、並列チャンク・Idle Timeout・
   * コールドスタートを取りこぼすため実費を大きく下回る。
   */
  gpu_source: 'runpod_actual' | 'estimated';
  gpu_unavailable_reason: string | null;
  gpu_by_endpoint: EndpointCost[];
  vps: number;
  supabase_prod: number;
  fixed_total: number;
  total: number;
}

interface AdminStats {
  total_users: number;
  active_users: number;
  total_transcriptions: number;
  total_hours_processed: number;
  monthly_revenue: number;
  monthly_subscription_revenue: number;
  monthly_oneshot_revenue: number;
  monthly_cost: number;
  monthly_cost_breakdown: CostBreakdown;
  monthly_profit: number;
  plan_stats: PlanStats[];
  average_processing_ratio: number;
  total_gpu_hours: number;
  monthly_processing_hours: number;
  monthly_history: MonthlyRevenue[];
  jpy_per_usd: number;
  gpu_cost_per_hour_usd: number;
  service_launch_month: string;
}

const PLAN_LABELS: Record<PlanType, string> = {
  free: '無料',
  lite: 'ライト',
  standard: 'スタンダード',
  unlimited: '無制限',
};

// プラン分布バーの塗り色（Badge variant の意味に整合した solid トークン）
const PLAN_BAR_COLOR: Record<PlanType, string> = {
  free: 'bg-gray-400',
  lite: 'bg-info',
  standard: 'bg-success',
  unlimited: 'bg-warning',
};

// 利益率 → Badge variant（意味整合: 高=success / 黒字=warning / 赤字=danger）
function marginVariant(margin: number): BadgeVariant {
  if (margin >= 50) return 'success';
  if (margin >= 0) return 'warning';
  return 'danger';
}

export default function AdminRevenuePage({ user }: AdminRevenuePageProps) {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportYear, setExportYear] = useState(new Date().getFullYear());
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() + 1);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
      setError('データの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const blob = await adminApi.exportRevenue(exportYear, exportMonth);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `otomochi_revenue_${exportYear}${String(exportMonth).padStart(2, '0')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to export CSV:', err);
      alert('CSVエクスポートに失敗しました');
    } finally {
      setExporting(false);
    }
  };

  // 換算レートはバックエンドの設定値を使う（フロントで別々にハードコードしない）
  const jpyPerUsd = stats?.jpy_per_usd ?? 150;
  // RunPod の請求が引けていないときは、GPU コストが実費を大幅に下回る推定値になる。
  // 数字をそのまま信じて価格判断されると危ないので、表示側で必ず区別する。
  const isEstimatedGpu = stats?.monthly_cost_breakdown.gpu_source === 'estimated';

  const calculateProfitMargin = (revenue: number, cost: number) => {
    const revenueUsd = revenue / jpyPerUsd;
    if (revenueUsd === 0) return 0;
    return ((revenueUsd - cost) / revenueUsd) * 100;
  };

  const planColumns: Column<PlanStats>[] = [
    { key: 'plan', header: 'プラン', render: (p) => <Badge variant={planToBadgeVariant(p.plan_type)}>{PLAN_LABELS[p.plan_type]}</Badge> },
    { key: 'price', header: '定価', align: 'right', render: (p) => `¥${p.monthly_price.toLocaleString()}` },
    { key: 'users', header: 'ユーザー', align: 'right', render: (p) => <span className="font-medium">{p.user_count}人</span> },
    { key: 'rev', header: '実収益', align: 'right', render: (p) => <span className="font-bold text-success">¥{p.total_revenue.toLocaleString()}</span> },
  ];

  const monthlyColumns: Column<MonthlyRevenue>[] = [
    { key: 'month', header: '月', render: (m) => <span className="font-medium">{m.month}</span> },
    { key: 'rev', header: '売上 (¥)', align: 'right', render: (m) => <span className="text-success">¥{m.total_revenue.toLocaleString()}</span> },
    { key: 'cost', header: 'コスト ($)', align: 'right', render: (m) => <span className="text-danger">${m.total_cost.toFixed(2)}</span> },
    {
      key: 'profit',
      header: '利益 ($)',
      align: 'right',
      render: (m) => {
        const profit = m.total_revenue / jpyPerUsd - m.total_cost;
        return <span className={`font-bold ${profit >= 0 ? 'text-info' : 'text-danger'}`}>${profit.toFixed(2)}</span>;
      },
    },
    { key: 'count', header: '書き起こし数', align: 'right', render: (m) => `${m.transcription_count}件` },
    {
      key: 'margin',
      header: '利益率',
      align: 'right',
      render: (m) => {
        const revenueUsd = m.total_revenue / jpyPerUsd;
        const margin = revenueUsd > 0 ? ((revenueUsd - m.total_cost) / revenueUsd) * 100 : 0;
        return <Badge variant={marginVariant(margin)}>{margin.toFixed(1)}%</Badge>;
      },
    },
  ];

  return (
    <Layout>
      <Header user={user} title="otomochi - 収益レポート" />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12">
            <Spinner size="lg" className="mx-auto" />
            <p className="mt-4 text-text-sub">読み込み中...</p>
          </div>
        ) : error ? (
          <ErrorState message={error} onRetry={loadStats} />
        ) : stats ? (
          <div className="space-y-8">
            {/* 今月のサマリー */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card>
                <div className="flex items-center gap-2 text-text-sub mb-2">
                  <DollarSign className="w-5 h-5" />
                  <span className="text-sm">月間売上</span>
                </div>
                <div className="text-3xl font-bold text-success">¥{stats.monthly_revenue.toLocaleString()}</div>
                <div className="text-sm text-text-sub mt-1">≈ ${(stats.monthly_revenue / jpyPerUsd).toFixed(2)} USD</div>
              </Card>

              <Card>
                <div className="flex items-center gap-2 text-text-sub mb-2">
                  <TrendingDown className="w-5 h-5" />
                  <span className="text-sm">月間コスト</span>
                </div>
                <div className="text-3xl font-bold text-danger">${stats.monthly_cost.toFixed(2)}</div>
                <div className="text-sm text-text-sub mt-1">
                  固定 ${stats.monthly_cost_breakdown.fixed_total.toFixed(2)} + GPU ${stats.monthly_cost_breakdown.gpu.toFixed(2)}
                  <span className="ml-1">{isEstimatedGpu ? '（GPUは推定）' : '（GPUは実費）'}</span>
                </div>
              </Card>

              <Card>
                <div className="flex items-center gap-2 text-text-sub mb-2">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-sm">月間利益</span>
                </div>
                <div className={`text-3xl font-bold ${stats.monthly_profit >= 0 ? 'text-info' : 'text-danger'}`}>
                  ${stats.monthly_profit.toFixed(2)}
                </div>
                <div className="text-sm text-text-sub mt-1">
                  利益率: {calculateProfitMargin(stats.monthly_revenue, stats.monthly_cost).toFixed(1)}%
                </div>
              </Card>

              <Card>
                <div className="flex items-center gap-2 text-text-sub mb-2">
                  <Calendar className="w-5 h-5" />
                  <span className="text-sm">売上の内訳</span>
                </div>
                <div className="text-3xl font-bold text-primary">
                  ¥{stats.monthly_subscription_revenue.toLocaleString()}
                </div>
                <div className="text-sm text-text-sub mt-1">
                  サブスク ・ ワンショット ¥{stats.monthly_oneshot_revenue.toLocaleString()}
                </div>
              </Card>
            </div>

            {/* プラン別収益詳細 */}
            <Card>
              <h3 className="text-xl font-bold mb-6">プラン別収益</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <ResponsiveTable
                  columns={planColumns}
                  rows={stats.plan_stats}
                  rowKey={(p) => p.plan_type}
                  caption="プラン別収益"
                  footer={
                    <div className="flex justify-between">
                      <span>合計</span>
                      <span>
                        {stats.plan_stats.reduce((sum, p) => sum + p.user_count, 0)}人 ・{' '}
                        <span className="text-success">¥{stats.monthly_revenue.toLocaleString()}</span>
                      </span>
                    </div>
                  }
                />

                <div>
                  <h4 className="font-medium mb-4">プラン分布</h4>
                  <div className="space-y-3">
                    {stats.plan_stats.map((plan) => {
                      const percentage = stats.total_users > 0 ? (plan.user_count / stats.total_users) * 100 : 0;
                      return (
                        <div key={plan.plan_type}>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{PLAN_LABELS[plan.plan_type]}</span>
                            <span>{percentage.toFixed(1)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className={`h-3 rounded-full ${PLAN_BAR_COLOR[plan.plan_type]}`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Card>

            {/* コスト詳細 */}
            <Card>
              <h3 className="text-xl font-bold mb-6">コスト詳細</h3>
              {isEstimatedGpu && (
                <div className="mb-6 p-4 rounded-field border border-danger/40 bg-danger/5">
                  <div className="font-medium text-danger mb-1">GPUコストは推定値です</div>
                  <p className="text-sm text-text-sub">
                    RunPod の請求を取得できなかったため、壁時計の処理時間から概算しています。
                    チャンク並列・Idle Timeout・コールドスタートを含まないため、
                    <strong>実費を大きく下回ります</strong>。
                    {stats.monthly_cost_breakdown.gpu_unavailable_reason && (
                      <span className="block mt-1 font-mono text-xs">
                        {stats.monthly_cost_breakdown.gpu_unavailable_reason}
                      </span>
                    )}
                  </p>
                </div>
              )}

              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="p-4 bg-background rounded-field">
                  <div className="text-sm text-text-sub mb-1">GPU使用時間</div>
                  <div className="text-2xl font-bold">{stats.total_gpu_hours.toFixed(2)}時間</div>
                  <div className="text-sm text-text-sub">
                    {isEstimatedGpu
                      ? '壁時計の処理時間で代用（実消費より少ない）'
                      : '実費から逆算・並列と待機時間を含む'}
                  </div>
                </div>
                <div className="p-4 bg-background rounded-field">
                  <div className="text-sm text-text-sub mb-1">処理時間（壁時計）</div>
                  <div className="text-2xl font-bold">{stats.monthly_processing_hours.toFixed(2)}時間</div>
                  <div className="text-sm text-text-sub">体感速度の指標・GPU時間とは別物</div>
                </div>
                <div className="p-4 bg-background rounded-field">
                  <div className="text-sm text-text-sub mb-1">処理速度比</div>
                  <div className="text-2xl font-bold">{stats.average_processing_ratio.toFixed(1)}x</div>
                  <div className="text-sm text-text-sub">音声時間 / 処理時間（壁時計）</div>
                </div>
              </div>

              <h4 className="font-medium mb-3">内訳</h4>
              <dl className="divide-y divide-border">
                <div className="flex justify-between py-2 text-sm">
                  <dt className="text-text-sub">
                    {isEstimatedGpu ? 'GPU（従量・RunPod・推定）' : 'GPU（従量・RunPod実費）'}
                  </dt>
                  <dd className="font-medium">${stats.monthly_cost_breakdown.gpu.toFixed(2)}</dd>
                </div>
                {stats.monthly_cost_breakdown.gpu_by_endpoint.map((endpoint) => (
                  <div key={endpoint.name} className="flex justify-between py-2 pl-6 text-sm">
                    <dt className="text-text-sub">
                      うち {endpoint.name}
                      {endpoint.gpu_hours > 0 && (
                        <span className="ml-2 text-xs">({endpoint.gpu_hours.toFixed(1)}時間)</span>
                      )}
                    </dt>
                    <dd>${endpoint.amount.toFixed(2)}</dd>
                  </div>
                ))}
                {[
                  { label: 'VPS（固定・Vultr）', value: stats.monthly_cost_breakdown.vps },
                  { label: 'Supabase prod（固定・Pro）', value: stats.monthly_cost_breakdown.supabase_prod },
                ].map((row) => (
                  <div key={row.label} className="flex justify-between py-2 text-sm">
                    <dt className="text-text-sub">{row.label}</dt>
                    <dd className="font-medium">${row.value.toFixed(2)}</dd>
                  </div>
                ))}
                <div className="flex justify-between py-2 font-bold">
                  <dt>合計</dt>
                  <dd className="text-danger">${stats.monthly_cost_breakdown.total.toFixed(2)}</dd>
                </div>
              </dl>
              <p className="text-xs text-text-sub mt-3">
                固定費は稼働の有無にかかわらず満額発生します（月の途中でも日割りしません）。
              </p>
              {!isEstimatedGpu && (
                <p className="text-xs text-text-sub mt-2">
                  GPU実費は RunPod アカウント全体の請求額です。他の指標と違い管理者を除外できないため、
                  動作確認や dev エンドポイントの消費も含まれます（1件あたり平均単価は
                  {' '}${stats.gpu_cost_per_hour_usd.toFixed(3)}/時間）。
                </p>
              )}
            </Card>

            {/* 月次推移 */}
            <Card>
              <h3 className="text-xl font-bold mb-6">月次推移</h3>
              <ResponsiveTable
                columns={monthlyColumns}
                rows={stats.monthly_history}
                rowKey={(m) => m.month}
                caption="月次推移"
                emptyMessage={`${stats.service_launch_month} の公開以降に実績が入ります`}
              />
            </Card>

            {/* CSVエクスポート */}
            <Card>
              <h3 className="text-xl font-bold mb-4">確定申告用エクスポート</h3>
              <p className="text-text-sub mb-4">
                月次の収益・コストデータをCSV形式でダウンロードできます。確定申告や経理処理にご利用ください。
              </p>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label htmlFor="export-year" className="block text-sm text-text-sub mb-1">年</label>
                  <div className="w-32">
                    <Select id="export-year" value={exportYear} onChange={(e) => setExportYear(Number(e.target.value))}>
                      {[2024, 2025, 2026].map((year) => (
                        <option key={year} value={year}>{year}年</option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div>
                  <label htmlFor="export-month" className="block text-sm text-text-sub mb-1">月</label>
                  <div className="w-32">
                    <Select id="export-month" value={exportMonth} onChange={(e) => setExportMonth(Number(e.target.value))}>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                        <option key={month} value={month}>{month}月</option>
                      ))}
                    </Select>
                  </div>
                </div>
                <Button onClick={handleExportCSV} loading={exporting} className="gap-2">
                  {exporting ? (
                    'ダウンロード中...'
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      CSVをダウンロード
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        ) : null}
      </main>
    </Layout>
  );
}
